export const CONCERN_GROUP_CATEGORIES = [
	"皮膚・被毛",
	"耳",
	"免疫・アレルギー",
	"お腹・腸",
	"口腔",
	"関節・運動器",
	"体重・代謝",
	"泌尿器",
	"肝臓・膵臓",
	"心臓",
	"呼吸器",
	"目",
	"脳・神経",
	"シニア",
	"ストレス・メンタル",
	"その他",
] as const;

export const CONCERN_GROUPS = [
	{
		category: "皮膚・被毛",
		details: [
			"アトピー性皮膚炎",
			"膿皮症",
			"マラセチア皮膚炎",
			"脂漏症",
			"乾燥肌",
			"フケ",
			"かゆみ",
			"脱毛",
			"趾間炎",
			"ホットスポット（急性湿性皮膚炎）",
			"毛艶の低下",
			"赤み",
		],
	},
	{
		category: "耳",
		details: ["外耳炎", "耳垢", "耳の臭い", "耳血腫", "慢性外耳炎中耳炎"],
	},
	{
		category: "免疫・アレルギー",
		details: [
			"アレルギー体質",
			"花粉症様症状",
			"免疫バランスの乱れ",
			"慢性炎症",
			"自己免疫疾患",
			"季節性のかゆみ",
		],
	},
	{
		category: "お腹・腸",
		details: [
			"軟便",
			"下痢",
			"便秘",
			"嘔吐",
			"お腹が弱い",
			"ガスが多い",
			"炎症性腸疾患（IBD）",
			"食物アレルギー",
			"胃腸炎",
			"食欲不振",
			"消化不良",
		],
	},
	{ category: "口腔", details: ["歯石", "歯周病", "口臭"] },
	{
		category: "関節・運動器",
		details: [
			"関節炎",
			"変形性関節症",
			"膝蓋骨脱臼（パテラ）",
			"股関節形成不全",
			"椎間板ヘルニア",
			"足を引きずる",
			"階段を嫌がる",
			"ジャンプしない",
			"シニア犬の運動機能低下",
		],
	},
	{
		category: "体重・代謝",
		details: ["肥満", "痩せ", "筋肉量低下", "糖尿病", "高脂血症"],
	},
	{
		category: "泌尿器",
		details: [
			"膀胱炎",
			"尿路結石",
			"頻尿",
			"血尿",
			"尿漏れ",
			"腎臓病",
			"慢性腎不全",
			"飲水量増加",
		],
	},
	{
		category: "肝臓・膵臓",
		details: ["肝機能低下", "脂肪肝", "胆泥症", "膵炎"],
	},
	{
		category: "心臓",
		details: ["僧帽弁閉鎖不全症", "心雑音", "心不全", "咳が出る", "息切れ"],
	},
	{
		category: "呼吸器",
		details: [
			"気管虚脱",
			"短頭種気道症候群",
			"慢性気管支炎",
			"咳",
			"いびき",
			"呼吸が荒い",
		],
	},
	{
		category: "目",
		details: [
			"涙やけ",
			"ドライアイ",
			"結膜炎",
			"白内障",
			"緑内障",
			"目やに",
			"視力低下",
		],
	},
	{
		category: "脳・神経",
		details: ["てんかん", "認知症", "前庭疾患", "ふらつき", "旋回行動"],
	},
	{
		category: "シニア",
		details: [
			"認知機能低下",
			"睡眠リズムの乱れ",
			"夜鳴き",
			"筋力低下",
			"食欲低下",
		],
	},
	{
		category: "ストレス・メンタル",
		details: [
			"分離不安",
			"不安症",
			"吠え",
			"雷恐怖症",
			"来客恐怖",
			"ストレス性下痢",
			"常同行動",
		],
	},
	{ category: "その他", details: ["その他"] },
] as const;

export type ConcernGroupCategory = (typeof CONCERN_GROUP_CATEGORIES)[number];
export type ConcernCategory =
	(typeof CONCERN_GROUPS)[number]["details"][number];

export const CONCERN_CATEGORIES = CONCERN_GROUPS.flatMap(
	(group) => group.details,
) as ConcernCategory[];

const legacyConcernMap = {
	皮膚トラブル: "その他",
	アレルギー: "その他",
	消化器: "その他",
	"関節・骨": "その他",
	"目・耳": "その他",
	環境アレルギー: "アレルギー体質",
	痒み: "かゆみ",
	お腹のお悩み: "お腹が弱い",
} as const satisfies Record<string, ConcernCategory>;

const concernGroupSet = new Set<string>(CONCERN_GROUP_CATEGORIES);
const concernDetailSet = new Set<string>(CONCERN_CATEGORIES);
const detailToGroup = new Map<string, ConcernGroupCategory>(
	CONCERN_GROUPS.flatMap((group) =>
		group.details.map((detail) => [detail, group.category] as const),
	),
);

export function normalizeConcernDetail(value: string): ConcernCategory | null {
	if (concernDetailSet.has(value)) return value as ConcernCategory;
	return legacyConcernMap[value as keyof typeof legacyConcernMap] ?? null;
}

export function concernGroupsForDetails(
	values: readonly string[],
): ConcernGroupCategory[] {
	const seen = new Set<ConcernGroupCategory>();
	for (const value of values) {
		if (concernGroupSet.has(value)) {
			seen.add(value as ConcernGroupCategory);
			continue;
		}
		const normalized = normalizeConcernDetail(value);
		const group = normalized ? detailToGroup.get(normalized) : null;
		if (group) seen.add(group);
	}
	return seen.size > 0 ? [...seen] : ["その他"];
}

/** Condition-detail UI vocab tied to selected major groups (not pet concern details). */
export const CONDITION_DETAIL_MAJOR_GROUPS = [
	"皮膚・被毛",
	"お腹・腸",
	"その他",
] as const;

export type ConditionDetailMajorGroup =
	(typeof CONDITION_DETAIL_MAJOR_GROUPS)[number];

export const SKIN_CONDITION_DETAILS = [
	"痒み",
	"赤み",
	"脱毛",
	"フケ",
	"ベタベタ",
	"乾燥",
	"ブツブツ",
	"ただれ",
	"皮膚・被毛：その他",
	"皮膚・被毛：異常なし",
] as const;

export const GUT_CONDITION_DETAILS = [
	"下痢軟便",
	"便秘",
	"血便",
	"粘膜便",
	"嘔吐",
	"おなら多め",
	"ゲップ多い",
	"腹痛",
	"お腹・腸：その他",
	"お腹・腸：異常なし",
] as const;

export const CONDITION_DETAILS = [
	...SKIN_CONDITION_DETAILS,
	...GUT_CONDITION_DETAILS,
	"その他：異常なし",
] as const;
